import axios from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { tz } from '@date-fns/tz';
import { buildApiUrl } from '../config/urlConfig.js';

const berlinTz = tz('Europe/Berlin');
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

const mapRequestError = (error) => {
	if (error.response) {
		const errorMessage = error.response.data?.message
			|| error.response.data?.error
			|| `HTTP Error ${error.response.status}`;
		throw new Error(errorMessage);
	}

	if (error.request) {
		throw new Error('Netzwerkfehler: Keine Antwort vom Server');
	}

	throw new Error(error.message || 'Unbekannter Fehler');
};

const normalizeInstantToIsoString = (value, fieldName) => {
	if (value === undefined || value === null || value === '') {
		return undefined;
	}

	const dateValue = value instanceof Date ? value : new Date(value);
	if (!isValid(dateValue)) {
		throw new Error(`Ungueltiger Datumswert fuer ${fieldName}`);
	}

	return dateValue.toISOString();
};

const normalizeCalendarDate = (value, fieldName) => {
	if (value === undefined || value === null || value === '') {
		return undefined;
	}

	if (typeof value === 'string' && CALENDAR_DATE.test(value)) {
		if (!isValid(parseISO(value))) {
			throw new Error(`Ungueltiger Datumswert fuer ${fieldName}`);
		}

		return value;
	}

	const dateValue = value instanceof Date ? value : new Date(value);
	if (!isValid(dateValue)) {
		throw new Error(`Ungueltiger Datumswert fuer ${fieldName}`);
	}

	return format(dateValue, 'yyyy-MM-dd', { in: berlinTz });
};

const requireObject = (value, message) => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		throw new Error(message);
	}
};

const isPlainObject = (value) => {
	return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

export const fetchStudents = async () => {
	try {
		const url = buildApiUrl('account', '/students');
		const response = await axios.get(url, {
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (response.data && Array.isArray(response.data.students)) {
			return response.data.students;
		}

		throw new Error('Ungueltige API-Antwort');
	} catch (error) {
		mapRequestError(error);
	}
};

export const fetchEmployees = async () => {
	try {
		const url = buildApiUrl('account', '/employees');
		const response = await axios.get(url, {
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (response.data && Array.isArray(response.data.employees)) {
			return {
				employees: response.data.employees,
				inactiveCount: typeof response.data.inactiveCount === 'number'
					? response.data.inactiveCount
					: 0,
			};
		}

		throw new Error('Ungueltige API-Antwort');
	} catch (error) {
		mapRequestError(error);
	}
};

export const fetchUserBookings = async (params = {}) => {
	requireObject(params, 'Parameter muessen als Objekt uebergeben werden');

	const { studentId, employeeId, from, until } = params;
	const hasStudentId = studentId !== undefined && studentId !== null && studentId !== '';
	const hasEmployeeId = employeeId !== undefined && employeeId !== null && employeeId !== '';

	if (hasStudentId === hasEmployeeId) {
		throw new Error('Genau eine der Angaben studentId oder employeeId ist erforderlich');
	}

	const payload = {
		...(hasStudentId ? { student_id: studentId } : { employee_id: employeeId })
	};

	const fromIso = normalizeInstantToIsoString(from, 'from');
	const untilIso = normalizeInstantToIsoString(until, 'until');
	if (fromIso) {
		payload.from = fromIso;
	}
	if (untilIso) {
		payload.until = untilIso;
	}

	try {
		const url = buildApiUrl('account', '/bookings');
		const response = await axios.post(url, payload, {
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (response.data && Array.isArray(response.data.bookings)) {
			return response.data.bookings;
		}

		throw new Error('Ungueltige API-Antwort');
	} catch (error) {
		mapRequestError(error);
	}
};

export const fetchStudentDetails = async (params = {}) => {
	requireObject(params, 'Parameter muessen als Objekt uebergeben werden');

	const { studentId } = params;
	if (studentId === undefined || studentId === null || studentId === '') {
		throw new Error('studentId ist erforderlich');
	}

	try {
		const url = buildApiUrl('account', '/student-details');
		const response = await axios.post(
			url,
			{ student_id: studentId },
			{
				headers: {
					'Content-Type': 'application/json'
				}
			}
		);

		if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
			return {
				enrolments: Array.isArray(response.data.enrolments) ? response.data.enrolments : [],
				activeBudgets: Array.isArray(response.data.activeBudgets) ? response.data.activeBudgets : [],
				pendingPointReturns: Array.isArray(response.data.pendingPointReturns)
					? response.data.pendingPointReturns
					: [],
				bookingGroups: Array.isArray(response.data.bookingGroups) ? response.data.bookingGroups : [],
				accessibleRooms: Array.isArray(response.data.accessibleRooms)
					? response.data.accessibleRooms
					: [],
				budgetHistory: Array.isArray(response.data.budgetHistory) ? response.data.budgetHistory : [],
				budgetHistoryMeta: isPlainObject(response.data.budgetHistoryMeta)
					? response.data.budgetHistoryMeta
					: null,
			};
		}

		throw new Error('Ungueltige API-Antwort');
	} catch (error) {
		mapRequestError(error);
	}
};

export const fetchAccountAnalytics = async (params = {}) => {
	requireObject(params, 'Parameter muessen als Objekt uebergeben werden');

	const fromIso = normalizeCalendarDate(params.from, 'from');
	const untilIso = normalizeCalendarDate(params.until, 'until');

	const payload = {};
	if (fromIso) {
		payload.from = fromIso;
	}
	if (untilIso) {
		payload.until = untilIso;
	}

	try {
		const url = buildApiUrl('account', '/analytics');
		const response = await axios.post(url, payload, {
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
			const byTypeRaw = response.data.byType;
			const cancellationsRaw = response.data.cancellations;
			const averagesRaw = response.data.averages;

			return {
				meta: response.data.meta || {},
				dailyTrend: Array.isArray(response.data.dailyTrend) ? response.data.dailyTrend : [],
				topRooms: Array.isArray(response.data.topRooms) ? response.data.topRooms : [],
				topUsers: {
					students: Array.isArray(response.data.topUsers?.students) ? response.data.topUsers.students : [],
					employees: Array.isArray(response.data.topUsers?.employees) ? response.data.topUsers.employees : [],
				},
				byType: isPlainObject(byTypeRaw)
					? {
						withPoints: Number(byTypeRaw.withPoints) || 0,
						withoutPoints: Number(byTypeRaw.withoutPoints) || 0,
						adHoc: Number(byTypeRaw.adHoc) || 0,
					}
					: null,
				topForeignBookers: Array.isArray(response.data.topForeignBookers)
					? response.data.topForeignBookers
					: null,
				cancellations: isPlainObject(cancellationsRaw)
					? {
						count: Number(cancellationsRaw.count) || 0,
						rate: Number(cancellationsRaw.rate) || 0,
					}
					: null,
				averages: isPlainObject(averagesRaw)
					? {
						bookingDurationMinutes: Number(averagesRaw.bookingDurationMinutes) || 0,
						bookingsPerActiveDay: Number(averagesRaw.bookingsPerActiveDay) || 0,
					}
					: null,
			};
		}

		throw new Error('Ungueltige API-Antwort');
	} catch (error) {
		mapRequestError(error);
	}
};
